import { useCallback, useContext, useEffect, useState } from "react";
import { MyContext } from "../../Context/MyProvider";
import secureLocalStorage from "react-secure-storage";
import axios from "axios";
import Loading from "../Loading";
import ImageLoad from "../ImageLoad";
import AddPatient from "../DocAppointment/AddPatient";
import { useTranslation } from "react-i18next";
import { LuPlus, LuPencil, LuCreditCard, LuShieldCheck } from "react-icons/lu";
import { IoCloseSharp } from "react-icons/io5";
import DigitalHealthCard from "./DigitalHealthCard";

const FamilyMembers = () => {
  const { ApiBaseUrl, ImgBaseUrl, patientStatus } = useContext(MyContext);

  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [familyMemberList, setFamilyMemberList] = useState([]);
  const [isView, setIsView] = useState(false);
  const [patientData, setPatientData] = useState(null);
  const [relationshipData, setRelationshipData] = useState([]);
  const [booldData, setBooldData] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);

  const HandleGetMember = useCallback(async () => {
    const Userid = secureLocalStorage.getItem("Userid");
    if (!Userid) return;

    axios
      .post(`${ApiBaseUrl}/customer/family_member_list`, { id: Userid })
      .then((res) => {
        setFamilyMemberList(res.data.family_member || []);
        setLoading(false);
      })
      .catch((error) => {
        console.error(error);
        setLoading(false);
      });

    axios
      .get(`${ApiBaseUrl}/customer/family_member_add_detail`)
      .then((res) => {
        const { relationship_list, blood_group_list } = res.data;
        setRelationshipData(relationship_list || []);
        setBooldData(blood_group_list || []);
      })
      .catch((error) => {
        console.error(error);
      });
  }, [ApiBaseUrl]);

  useEffect(() => {
    HandleGetMember();
  }, [patientStatus, HandleGetMember]);

  const HandleGetPatientDetails = (id) => {
    axios
      .post(`${ApiBaseUrl}/customer/family_member_detail`, { fid: id })
      .then((res) => {
        setPatientData(res.data.data);
        setIsView(true);
      })
      .catch((error) => {
        console.error(error);
      });
  };

  return (
    <div className="family-members-container w-full min-h-screen pb-[100px] content-wrapper px-4 md:px-6 max-w-7xl mx-auto">
      {loading ? (
        <Loading
          CustomeStyle="fixed left-0 top-0 h-[100vh] w-full flex items-center justify-center bg-white z-[9999]"
          index={0}
        />
      ) : (
        <div className="flex flex-col py-5">
          {/* Page Info Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
            <div className="leading-2">
              <h1 className="text-3xl font-extrabold text-slate-900 tracking-tight mb-1">
                {t("Family Members")}
              </h1>
              <p className="text-slate-500 font-medium text-sm">
                {t("Manage your family profiles for quick and easy bookings.")}
              </p>
            </div>
            <button
              onClick={() => {
                setIsView(true);
                setPatientData(null);
              }}
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-2xl text-sm font-bold transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 text-white shadow-md hover:shadow-lg active:scale-95 h-12 px-6 py-2 bg-indigo-600 hover:bg-indigo-700 w-full sm:w-fit"
            >
              <LuPlus className="w-5 h-5" />
              {t("Add Member")}
            </button>
          </div>

          {/* Grid Layout */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Family Members Cards */}
            {familyMemberList.map((item, index) => {
              return (
                <div
                  key={index}
                  className="rounded-3xl border border-slate-150 shadow-sm hover:shadow-md hover:border-indigo-200 transition-all duration-300 h-full overflow-hidden relative bg-white flex flex-col justify-between group"
                >
                  {/* Decorative design element */}
                  <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-indigo-500/5 to-transparent rounded-full -mr-12 -mt-12 group-hover:scale-150 transition-transform duration-500 pointer-events-none"></div>

                  <div className="p-6 relative z-10 flex-1 flex flex-col justify-between">
                    <div className="flex flex-col items-center text-center mb-6">
                      {/* Photo Avatar */}
                      <div className="w-24 h-24 rounded-full bg-slate-50 flex items-center justify-center overflow-hidden ring-4 ring-indigo-500/5 group-hover:ring-indigo-500/10 transition-all shadow-md mb-4 border-2 border-white">
                        <ImageLoad
                          src={ImgBaseUrl + item.profile_image}
                          CustomeStyle="w-full h-full object-cover"
                          alt={item.name}
                        />
                      </div>
                      <h3 className="text-xl font-extrabold text-slate-800 mb-1.5 leading-snug">
                        {item.name}
                      </h3>
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-[10px] font-bold bg-emerald-50 text-emerald-600 border border-emerald-100 uppercase tracking-wider">
                        {item.reletion_name}
                      </span>
                    </div>

                    <div className="pt-4 border-t border-slate-100 flex flex-col gap-2.5">
                      {/* Edit Details Action */}
                      <button
                        onClick={() => HandleGetPatientDetails(item.id)}
                        className="w-full inline-flex items-center justify-center gap-2 py-2.5 px-4 rounded-2xl text-sm font-bold border border-slate-200 text-slate-700 hover:bg-slate-50 transition-all duration-200"
                      >
                        <LuPencil className="w-4 h-4 text-slate-400" />
                        {t("Edit Details")}
                      </button>

                      {/* Digital Health Card Action */}
                      <button
                        onClick={() => setSelectedPatient(item)}
                        className="w-full inline-flex items-center justify-center gap-2 py-3 px-4 rounded-2xl text-sm font-bold bg-[--button-color] text-white hover:shadow-lg hover:shadow-indigo-100 transition-all duration-200 active:scale-[0.98]"
                      >
                        <LuCreditCard className="w-4.5 h-4.5" />
                        {t("Digital Health Card")}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {isView && (
        <div
          onClick={() => setIsView(false)}
          className="flex bg-slate-900/60 backdrop-blur-sm h-full justify-center w-full fixed items-center left-0 top-0 z-[999] p-4 animate-in fade-in duration-200"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-[28px] shadow-2xl max-w-lg w-full max-h-[85vh] scroll-container p-6 relative"
          >
            <button
              onClick={() => setIsView(false)}
              className="absolute top-4 right-4 p-2 rounded-full text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-all duration-200 z-10"
              aria-label="Close modal"
            >
              <IoCloseSharp className="w-5 h-5" />
            </button>

            <AddPatient
              Relation={relationshipData}
              BooldType={booldData}
              Data={patientData}
            />
          </div>
        </div>
      )}

      {selectedPatient && (
        <div
          onClick={() => setSelectedPatient(null)}
          className="flex bg-slate-900/60 backdrop-blur-sm h-full justify-center w-full fixed items-center left-0 top-0 z-[999] p-4 animate-in fade-in duration-200"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-[28px] shadow-2xl max-w-md w-full p-6 relative border border-slate-100/50"
          >
            <DigitalHealthCard patientData={selectedPatient} />
          </div>
        </div>
      )}
    </div>
  );
};

export default FamilyMembers;
